import {Dispatch, useCallback, useState} from 'react';
import {MaybeNull} from '#common/types';

export interface UseStorageOptions {
  storeInitialValue?: boolean;
}

export type UseStorageResult<T> = [MaybeNull<T>, Dispatch<MaybeNull<T>>];

export function useStorage<T>(
  storage: Storage,
  key: string,
  initialValue?: T,
  options?: UseStorageOptions,
): UseStorageResult<T> {
  const storeOrDelete = useCallback(
    (value: MaybeNull<T>) => {
      if (value === null) {
        storage.removeItem(key);
      } else {
        storage.setItem(key, JSON.stringify(value));
      }
    },
    [key, storage],
  );

  const [storedValue, setStoredValue] = useState<MaybeNull<T>>(() => {
    /* v8 ignore if -- @preserve */
    if (typeof window === 'undefined') {
      return initialValue;
    }
    try {
      const value = storage.getItem(key);
      if (typeof value === 'string') {
        return JSON.parse(value); // eslint-disable-line @typescript-eslint/no-unsafe-return
      } else if (options?.storeInitialValue && initialValue !== undefined) {
        storeOrDelete(initialValue);
        return initialValue;
      } else {
        return initialValue;
      }
    } catch (_error) {
      /* v8 ignore next -- @preserve */
      if (initialValue !== undefined) {
        storeOrDelete(initialValue);
      }
      /* v8 ignore next -- @preserve */
      return initialValue;
    }
  });

  const setValue = useCallback(
    (value: MaybeNull<T>) => {
      setStoredValue(value);
      /* v8 ignore next -- @preserve */
      if (typeof window !== 'undefined') {
        try {
          storeOrDelete(value);
        } catch (_error) {
          // Failed to serialize or write to storage
        }
      }
    },
    [setStoredValue, storeOrDelete],
  );

  return [storedValue, setValue];
}
